import React, { useCallback, useState } from 'react';
import { DeleteOutlined, InboxOutlined, RotateRightOutlined, SwapOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Space, Upload, message, Row, Col, Dropdown, Menu } from 'antd';
import useStore from '../../../stores/useStore';

const { Dragger } = Upload;

const FileUploaderControl = () => {
    const {
        backgrounds,
        currentBackgroundId,
        addBackgroundFromImage,
        setRotation,
        rotation,
        toggleFlipX,
        toggleFlipY,
        clearCurrentBackground,
        clearAllBackgrounds,
        removeBackground,
        selectBackground,
        nextBackground,
        previousBackground,
        rotateBackground
    } = useStore();

    const [uploading, setUploading] = useState(false);

    const beforeUpload = useCallback((file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('Можно загружать только изображения!');
            return Upload.LIST_IGNORE;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            addBackgroundFromImage(reader.result, file.name);
            message.success(`${file.name} успешно загружен`);
        };
        return false;
    }, [addBackgroundFromImage]);

    const handleRotate = () => {
        rotateBackground();
    };

    const handleClearAll = () => {
        if (backgrounds.length > 0) {
            clearAllBackgrounds();
            message.success('Все фоны удалены');
        }
    };

    const handleRemoveCurrent = () => {
        if (currentBackgroundId) {
            removeBackground(currentBackgroundId);
            message.success('Текущий фон удален');
        }
    };

    const backgroundsMenu = (
        <Menu>
            {backgrounds.map(bg => (
                <Menu.Item
                    key={bg.id}
                    onClick={() => selectBackground(bg.id)}
                    className={bg.id === currentBackgroundId ? 'ant-menu-item-selected' : ''}
                >
                    {bg.name || `Фон ${bg.id.slice(-4)}`}
                </Menu.Item>
            ))}
            {backgrounds.length > 0 && [
                <Menu.Divider key="divider" />,
                <Menu.Item key="clear-all" onClick={handleClearAll} danger>
                    Удалить все
                </Menu.Item>
            ]}
        </Menu>
    );

    const currentBackground = backgrounds.find(bg => bg.id === currentBackgroundId);

    return (
        <Space className="FileUploader" direction="vertical" style={{ width: '100%' }}>
            <Dragger
                beforeUpload={beforeUpload}
                showUploadList={false}
                accept="image/*"
                multiple={true}
                disabled={uploading}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Перетащите файлы или выберите их</p>
                <p className="ant-upload-hint">
                    Поддерживается загрузка нескольких изображений.
                </p>
            </Dragger>

            {backgrounds.length > 0 && (
                <div className="backgrounds-management-panel" style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '12px',
                    width: '100%'
                }}>
                    <Row gutter={[8, 8]} align="middle">
                        <Col span={24}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                            }}>
                                <span>
                                    <strong>Фоны:</strong> {backgrounds.length}
                                    {currentBackground && ` (Текущий: ${currentBackground.name || 'Без имени'})`}
                                </span>
                                <Dropdown overlay={backgroundsMenu} trigger={['click']}>
                                    <Button size="small">Список фонов</Button>
                                </Dropdown>
                            </div>
                        </Col>

                        <Col span={24}>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<LeftOutlined />}
                                    onClick={previousBackground}
                                    disabled={backgrounds.length <= 1}
                                >
                                    Предыдущий
                                </Button>

                                <span>
                                    {backgrounds.findIndex(bg => bg.id === currentBackgroundId) + 1} / {backgrounds.length}
                                </span>

                                <Button
                                    type="default"
                                    size="small"
                                    icon={<RightOutlined />}
                                    onClick={nextBackground}
                                    disabled={backgrounds.length <= 1}
                                >
                                    Следующий
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>
            )}

            <div style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'center', margin: '8px 0' }}>
                    <Button
                        className="FileUploader-button button-rotate"
                        type="default"
                        onClick={handleRotate}
                        block
                        disabled={!currentBackgroundId}
                        title="Повернуть по часовой"
                    >
                        <RotateRightOutlined />
                    </Button>
                    <Button
                        type="default"
                        icon={<SwapOutlined />}
                        onClick={toggleFlipX}
                        disabled={!currentBackgroundId}
                        title="Отзеркалить по X"
                    />
                    <Button
                        type="default"
                        icon={<SwapOutlined rotate={90} />}
                        onClick={toggleFlipY}
                        disabled={!currentBackgroundId}
                        title="Отзеркалить по Y"
                    />
                </Space>

                <Row gutter={8}>
                    <Col span={12}>
                        <Button
                            className="FileUploader-button"
                            type="primary"
                            danger
                            onClick={handleRemoveCurrent}
                            block
                            disabled={!currentBackgroundId}
                        >
                            Удалить текущий <DeleteOutlined />
                        </Button>
                    </Col>
                    <Col span={12}>
                        <Button
                            className="FileUploader-button"
                            type="default"
                            danger
                            onClick={handleClearAll}
                            block
                            disabled={backgrounds.length === 0}
                        >
                            Удалить все <DeleteOutlined />
                        </Button>
                    </Col>
                </Row>
            </div>
        </Space>
    );
};

export default FileUploaderControl;